import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, XCircle, MapPin, CreditCard, Package } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";

const STEP_KEYS = ["step_placed", "step_confirmed", "step_shipped", "step_delivered"];
const STATUS_INDEX = { pending: 0, placed: 0, open: 0, confirmed: 1, paid: 1, processing: 1, shipped: 2, in_transit: 2, delivered: 3, completed: 3 };

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const { fmt } = useCurrency();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const load = () => api(`/orders/${id}`, { auth: true }).then(setOrder).catch((e) => setError(e.message));

  useEffect(() => {
    if (!user) {
      navigate(`/login?next=/orders/${id}`);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  if (!user) return null;

  if (error)
    return (
      <div data-testid="order-error" className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="font-heading text-xl font-semibold text-slate-800">{t("empty_t")}</p>
        <Link to="/account" className="mt-6 inline-block rounded-full bg-teal-700 px-5 py-2.5 text-sm font-semibold text-white">
          {t("back_account")}
        </Link>
      </div>
    );

  if (!order)
    return (
      <div data-testid="order-loading" className="max-w-3xl mx-auto px-4 py-16 animate-pulse space-y-4">
        <div className="h-8 w-1/2 rounded bg-slate-100" />
        <div className="h-40 rounded-2xl bg-slate-100" />
      </div>
    );

  const cancelled = order.status === "cancelled";
  const stepIdx = STATUS_INDEX[order.status] ?? 0;
  const items = order.items?.length ? order.items : order.product_title || order.title ? [order] : [];
  const tracking = order.tracking || order.tracking_events || [];

  const cancel = async () => {
    const reason = window.prompt(t("cancel_order") + "?");
    if (reason === null) return;
    setCancelling(true);
    try {
      await api(`/orders/${id}/cancel`, { method: "POST", auth: true, body: { reason: reason || "Buyer cancelled" } });
      toast.success(t("step_cancelled"));
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div data-testid="order-detail-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/account" className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:underline">
        <ArrowLeft className="h-4 w-4" /> {t("back_account")}
      </Link>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          {t("order_t")} #{String(order.id || id).slice(-8).toUpperCase()}
        </h1>
        {order.status && (
          <span data-testid="order-status" className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${
            cancelled ? "bg-red-100 text-red-700" : "bg-teal-100 text-teal-800"
          }`}>
            {cancelled ? t("step_cancelled") : order.status}
          </span>
        )}
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
        {cancelled ? (
          <p data-testid="order-cancelled-note" className="flex items-center gap-2 text-sm font-semibold text-red-600">
            <XCircle className="h-5 w-5" /> {t("step_cancelled")}
          </p>
        ) : (
          <div className="flex items-center">
            {STEP_KEYS.map((k, i) => (
              <div key={k} className="flex flex-1 items-center last:flex-none">
                <div className="flex flex-col items-center">
                  <div
                    data-testid={`order-step-${k}`}
                    className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold ${
                      i <= stepIdx ? "border-teal-600 bg-teal-600 text-white" : "border-slate-300 text-slate-400"
                    }`}
                  >
                    {i < stepIdx ? <Check className="h-4 w-4" /> : i + 1}
                  </div>
                  <span className={`mt-1.5 text-[10px] font-semibold whitespace-nowrap ${i <= stepIdx ? "text-teal-700" : "text-slate-400"}`}>
                    {t(k)}
                  </span>
                </div>
                {i < STEP_KEYS.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-1 mb-5 rounded ${i < stepIdx ? "bg-teal-600" : "bg-slate-200"}`} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {tracking.length > 0 && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading font-bold text-slate-900">{t("tracking_t")}</h2>
          <div className="mt-3 space-y-3">
            {tracking.map((ev, i) => (
              <div key={ev.id || i} data-testid={`tracking-event-${i}`} className="flex gap-3 text-sm">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-teal-600" />
                <div>
                  <p className="font-medium text-slate-800">{ev.text || ev.note || ev.status}</p>
                  <p className="text-xs text-slate-400">{ev.created_at ? new Date(ev.created_at).toLocaleString() : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-heading font-bold text-slate-900">{t("items_t")}</h2>
        <div className="mt-3 divide-y divide-slate-100">
          {items.map((it, i) => (
            <div key={it.id || i} data-testid={`order-item-${i}`} className="flex items-center gap-3 py-3 text-sm">
              <Package className="h-4 w-4 shrink-0 text-teal-600" />
              <span className="flex-1 truncate font-medium text-slate-800">{it.title || it.product_title || it.product_name}</span>
              {it.quantity != null && <span className="text-slate-500">× {it.quantity}</span>}
              {(it.price != null || it.total != null) && (
                <span className="font-semibold text-teal-700">{fmt(it.total ?? it.price)}</span>
              )}
            </div>
          ))}
        </div>
        {order.total != null && (
          <div className="mt-3 flex justify-between border-t border-slate-100 pt-3 text-sm">
            <span className="font-semibold text-slate-700">{t("total_t")}</span>
            <span data-testid="order-total" className="font-extrabold text-teal-700">{fmt(order.total)}</span>
          </div>
        )}
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {order.delivery && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="font-heading flex items-center gap-2 font-bold text-slate-900">
              <MapPin className="h-4 w-4 text-teal-600" /> {t("shipping_t")}
            </h2>
            <p className="mt-2 text-sm text-slate-600">{order.delivery.name} · {order.delivery.phone}</p>
            <p className="text-sm text-slate-600">
              {order.delivery.address}, {order.delivery.city}{order.delivery.country ? `, ${order.delivery.country}` : ""}
            </p>
          </div>
        )}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading flex items-center gap-2 font-bold text-slate-900">
            <CreditCard className="h-4 w-4 text-teal-600" /> {t("payment_t")}
          </h2>
          <p className="mt-2 text-sm text-slate-600 capitalize">
            {order.payment_method || "—"}{order.payment_status ? ` · ${order.payment_status}` : ""}
          </p>
        </div>
      </div>

      {!cancelled && ["pending", "placed", "open"].includes(order.status) && (
        <button
          data-testid="order-cancel-button"
          onClick={cancel}
          disabled={cancelling}
          className="mt-6 rounded-full border border-red-300 px-5 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 transition disabled:opacity-60"
        >
          {t("cancel_order")}
        </button>
      )}
    </div>
  );
}
